import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const CURRENCIES = ['USD','GBP','EUR','THB','MYR','AUD','CAD','SGD','AED','NOK','SEK','CHF']
const CURRENCY_SYMBOLS = { USD:'$', GBP:'£', EUR:'€', THB:'฿', MYR:'RM ', AUD:'A$', CAD:'C$', SGD:'S$', AED:'AED ', NOK:'kr', SEK:'kr', CHF:'CHF ' }

export function Invoices() {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    supabase.from('invoices').select('*, clients(first_name, last_name)').order('created_at', { ascending: false })
      .then(({ data }) => { setInvoices(data || []); setLoading(false) })
  }, [])

  const STATUS_COLORS = { draft:'gray', sent:'blue', paid:'green', partial:'amber', overdue:'red', cancelled:'gray' }

  return (
    <div>
      <div className="page-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="page-title">Invoices</h1>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/invoices/new')}><Plus size={16} /> New</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
        : invoices.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px' }}>
            <p className="empty-state-title">No invoices yet</p>
            <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => navigate('/invoices/new')}><Plus size={16} /> Create Invoice</button>
          </div>
        ) : invoices.map(inv => (
          <div key={inv.id} className="list-item" onClick={() => navigate(`/invoices/${inv.id}`)}>
            <div className="list-item-avatar" style={{ borderRadius: '8px', fontSize: '12px', fontWeight: '700', width: '38px', height: '38px' }}>
              {inv.currency}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{inv.invoice_number}</div>
              <div className="list-item-sub">
                {inv.clients ? `${inv.clients.first_name || ''} ${inv.clients.last_name || ''}`.trim() : 'No client'}
                {inv.issue_date ? ` · ${new Date(inv.issue_date).toLocaleDateString()}` : ''}
              </div>
            </div>
            <div className="list-item-right">
              <span className={`badge badge-${STATUS_COLORS[inv.status]}`}>{inv.status}</span>
              {inv.total && <span style={{ fontSize: '13px', fontWeight: '600' }}>{CURRENCY_SYMBOLS[inv.currency] || ''}{Number(inv.total).toLocaleString()}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function NewInvoice() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [clientResults, setClientResults] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [lineItems, setLineItems] = useState([
    { description: '', qty: 1, unit_price: '', show_price: true }
  ])
  const [form, setForm] = useState({
    currency: 'USD', show_unit_price: true, show_total: true,
    discount: '', discount_type: 'fixed',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '', notes: '', status: 'draft'
  })

  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('clients').select('id, first_name, last_name, email, phone, country, city')
        .or(`first_name.ilike.%${clientSearch}%,last_name.ilike.%${clientSearch}%,email.ilike.%${clientSearch}%`).limit(5)
      setClientResults(data || [])
    }, 300)
    return () => clearTimeout(t)
  }, [clientSearch])

  function setField(k, v) { setForm(p => ({ ...p, [k]: v })) }
  function setItem(i, k, v) { setLineItems(p => p.map((item, idx) => idx === i ? { ...item, [k]: v } : item)) }
  function addItem() { setLineItems(p => [...p, { description: '', qty: 1, unit_price: '', show_price: true }]) }
  function removeItem(i) { setLineItems(p => p.filter((_, idx) => idx !== i)) }

  const sym = CURRENCY_SYMBOLS[form.currency] || ''
  const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.qty || 0) * Number(item.unit_price || 0)), 0)
  const discountAmt = form.discount_type === 'percent' ? subtotal * Number(form.discount || 0) / 100 : Number(form.discount || 0)
  const total = Math.max(0, subtotal - discountAmt)

  async function handleSave(status = 'draft') {
    setSaving(true)
    const payload = {
      ...form, status,
      client_id: selectedClient?.id || null,
      line_items: lineItems,
      subtotal, total,
      discount: Number(form.discount || 0),
      balance_due: total,
      created_by: profile?.id,
      due_date: form.due_date || null,
    }
    const { data, error } = await supabase.from('invoices').insert(payload).select().single()
    setSaving(false)
    if (!error) navigate('/invoices')
    else alert('Error: ' + error.message)
  }

  function generatePDF() {
    const doc = new jsPDF()
    // Header
    doc.setFontSize(28)
    doc.setFont('helvetica', 'bold')
    doc.text("Jackie's Custom Tailor", 14, 25)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120)
    doc.text('Est. Bangkok, Thailand · 1969', 14, 32)
    doc.text(`Invoice #: DRAFT`, 140, 20)
    doc.text(`Date: ${form.issue_date}`, 140, 27)
    if (form.due_date) doc.text(`Due: ${form.due_date}`, 140, 34)
    // Client
    if (selectedClient) {
      doc.setTextColor(0)
      doc.setFontSize(11)
      doc.text('Bill To:', 14, 48)
      doc.setFontSize(10)
      const clientName = [selectedClient.first_name, selectedClient.last_name].filter(Boolean).join(' ')
      doc.text(clientName, 14, 55)
      if (selectedClient.email) doc.text(selectedClient.email, 14, 61)
      if (selectedClient.country) doc.text([selectedClient.city, selectedClient.country].filter(Boolean).join(', '), 14, 67)
    }
    // Table
    const tableColumns = form.show_unit_price
      ? ['Description', 'Qty', `Unit Price (${form.currency})`, `Amount (${form.currency})`]
      : ['Description', 'Qty']
    const tableRows = lineItems.map(item => {
      const amount = Number(item.qty || 0) * Number(item.unit_price || 0)
      if (form.show_unit_price && item.show_price) {
        return [item.description, item.qty, `${sym}${Number(item.unit_price || 0).toLocaleString()}`, `${sym}${amount.toLocaleString()}`]
      }
      return [item.description, item.qty, '—', '—']
    })
    doc.autoTable({
      head: [tableColumns], body: tableRows,
      startY: selectedClient ? 75 : 48,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [26, 14, 8], textColor: [184, 150, 62] }
    })
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10
    if (form.show_total) {
      if (form.discount) {
        doc.text(`Subtotal: ${sym}${subtotal.toLocaleString()}`, 140, finalY)
        doc.text(`Discount: -${sym}${discountAmt.toLocaleString()}`, 140, finalY + 6)
      }
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(`Total: ${sym}${total.toLocaleString()} ${form.currency}`, 140, finalY + (form.discount ? 14 : 0))
    }
    if (form.notes) {
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text('Notes:', 14, finalY)
      doc.text(form.notes, 14, finalY + 6)
    }
    doc.save(`Invoice-${selectedClient?.last_name || 'Draft'}-${form.issue_date}.pdf`)
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">New Invoice</h1>
        <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowPreview(p => !p)}>
          {showPreview ? <EyeOff size={16} /> : <Eye size={16} />} {showPreview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {showPreview ? (
        /* ── PREVIEW ── */
        <div className="invoice-preview fade-in">
          <div className="invoice-header">
            <div>
              <div className="invoice-logo">Jackie's Custom Tailor</div>
              <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '2px' }}>Est. Bangkok, 1969</div>
            </div>
            <div className="invoice-number">
              <div style={{ fontWeight: '700', fontSize: '14px' }}>INVOICE</div>
              <div style={{ color: 'var(--gray-400)', marginTop: '2px' }}>Draft</div>
              <div>{form.issue_date}</div>
              {form.due_date && <div>Due: {form.due_date}</div>}
            </div>
          </div>
          {selectedClient && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--gray-500)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Bill To</div>
              <div style={{ fontWeight: '600' }}>{selectedClient.first_name} {selectedClient.last_name}</div>
              {selectedClient.email && <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{selectedClient.email}</div>}
              {selectedClient.country && <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{[selectedClient.city, selectedClient.country].filter(Boolean).join(', ')}</div>}
            </div>
          )}
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center', width: '40px' }}>Qty</th>
                {form.show_unit_price && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                {form.show_total && <th style={{ textAlign: 'right' }}>Amount</th>}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i}>
                  <td>{item.description || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{item.qty}</td>
                  {form.show_unit_price && <td style={{ textAlign: 'right' }}>{item.show_price ? `${sym}${Number(item.unit_price||0).toLocaleString()}` : '—'}</td>}
                  {form.show_total && <td style={{ textAlign: 'right' }}>{item.show_price ? `${sym}${(Number(item.qty||0)*Number(item.unit_price||0)).toLocaleString()}` : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {form.show_total && (
            <div style={{ borderTop: '2px solid var(--gray-200)', paddingTop: '12px' }}>
              {form.discount && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--gray-500)', marginBottom: '4px' }}><span>Subtotal</span><span>{sym}{subtotal.toLocaleString()}</span></div>}
              {form.discount && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--red)', marginBottom: '4px' }}><span>Discount</span><span>-{sym}{discountAmt.toLocaleString()}</span></div>}
              <div className="invoice-total">{sym}{total.toLocaleString()} {form.currency}</div>
            </div>
          )}
          {form.notes && <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--gray-500)', fontStyle: 'italic' }}>{form.notes}</p>}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleSave('sent')} disabled={saving}>{saving ? 'Saving…' : '✓ Save & Send'}</button>
            <button className="btn btn-outline" onClick={generatePDF}>↓ PDF</button>
          </div>
        </div>
      ) : (
        /* ── FORM ── */
        <div className="fade-in">
          {/* Client */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Client</div>
            {selectedClient ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600' }}>{selectedClient.first_name} {selectedClient.last_name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{selectedClient.email}</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setSelectedClient(null)}>Change</button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div className="search-bar">
                  <input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Search client…" />
                </div>
                {clientResults.length > 0 && (
                  <div style={{ border: '1px solid var(--gray-200)', borderRadius: '8px', overflow: 'hidden', marginTop: '4px', background: 'white', boxShadow: 'var(--shadow-md)' }}>
                    {clientResults.map(c => (
                      <div key={c.id} onClick={() => { setSelectedClient(c); setClientSearch(''); setClientResults([]) }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}>
                        <div style={{ fontWeight: '500' }}>{c.first_name} {c.last_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{c.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Currency & dates */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Invoice Settings</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-select" value={form.currency} onChange={e => setField('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input className="form-input" type="date" value={form.issue_date} onChange={e => setField('issue_date', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Due Date <span>(optional)</span></label>
                <input className="form-input" type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                  {['draft','sent','paid','partial','overdue'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </div>
            </div>
            {/* Show/hide toggles */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--gray-700)', cursor: 'pointer', flex: 1 }}>
                <input type="checkbox" checked={form.show_unit_price} onChange={e => setField('show_unit_price', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--brown-dark)' }} />
                Show unit prices
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--gray-700)', cursor: 'pointer', flex: 1 }}>
                <input type="checkbox" checked={form.show_total} onChange={e => setField('show_total', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--brown-dark)' }} />
                Show total
              </label>
            </div>
          </div>

          {/* Line items */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Line Items</div>
            {lineItems.map((item, i) => (
              <div key={i} style={{ border: '1px solid var(--gray-100)', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: 'var(--gray-50)' }}>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={item.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="e.g. Bespoke Navy Suit" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Qty</label>
                    <input className="form-input" type="number" min="1" value={item.qty} onChange={e => setItem(i, 'qty', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Price</label>
                    <input className="form-input" type="number" step="0.01" value={item.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--gray-600)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={item.show_price} onChange={e => setItem(i, 'show_price', e.target.checked)} style={{ accentColor: 'var(--brown-dark)' }} />
                    Show price for this item
                  </label>
                  {lineItems.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="btn btn-danger btn-sm">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline" onClick={addItem} style={{ width: '100%' }}>
              <Plus size={16} /> Add Item
            </button>
          </div>

          {/* Discount & notes */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="form-section-title">Discount & Notes</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Discount</label>
                <input className="form-input" type="number" step="0.01" value={form.discount} onChange={e => setField('discount', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-select" value={form.discount_type} onChange={e => setField('discount_type', e.target.value)}>
                  <option value="fixed">Fixed ({sym})</option>
                  <option value="percent">Percent (%)</option>
                </select>
              </div>
            </div>
            {/* Total display */}
            <div style={{ background: 'var(--brown-dark)', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px' }}>
              {form.discount && Number(form.discount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                  <span>Subtotal</span><span>{sym}{subtotal.toLocaleString()}</span>
                </div>
              )}
              {form.discount && Number(form.discount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#FCA5A5', marginBottom: '6px' }}>
                  <span>Discount</span><span>-{sym}{discountAmt.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '700', color: 'var(--gold)' }}>
                <span>Total</span>
                <span>{sym}{total.toLocaleString()} {form.currency}</span>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Notes <span>(shown on invoice)</span></label>
              <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Payment instructions, thank you note, etc." />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
            <button className="btn btn-outline btn-lg" onClick={() => handleSave('draft')} disabled={saving} style={{ borderRadius: '12px' }}>Save Draft</button>
            <button className="btn btn-primary btn-lg" onClick={() => setShowPreview(true)} style={{ borderRadius: '12px' }}>Preview →</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Invoices
